import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { tokens, couleursStatut, couleursGravite, couleursImpact, couleursType } from '../tokens.js';
import { Badge, formaterDateRelative } from '../helpers.jsx';
import {
  Clock, AlertTriangle, AlertOctagon, CheckCircle,
  TrendingUp, Inbox, Edit3, Activity, AlertCircle, Archive,
} from 'lucide-react';

export function AdminTableauBord({ onOuvrirTicket }) {
  const [data, setData]         = useState(null);
  const [chargement, setCh]     = useState(true);
  const [erreur, setErreur]     = useState('');

  useEffect(() => {
    setCh(true);
    api.get('/admin/tableau-bord')
      .then(d => setData(d))
      .catch(e => setErreur(e.message))
      .finally(() => setCh(false));
  }, []);

  if (chargement) return <div style={etat}>Chargement…</div>;
  if (erreur || !data) return <div style={{ ...etat, color: tokens.danger }}>{erreur || 'Erreur'}</div>;

  const { parametres: p, kpi, derniers_crees, derniers_modifies, derniers_clos, en_souffrance, histogramme_mensuel } = data;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
          Tableau de bord
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
          Vue d'ensemble · seuils paramétrables dans Administration &gt; Paramètres.
        </p>
      </div>

      {/* ===== KPI ===== */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12, marginBottom: 22,
      }}>
        <Kpi
          Icon={Clock}
          libelle="Délai moyen de traitement"
          valeur={formaterDuree(kpi.delai_moyen_traitement_minutes)}
          aide={`Tickets clos (${kpi.total_resolus + (kpi.total_ouverts === 0 ? 0 : 0)} au total)`}
          couleur={tokens.accent}
        />
        <Kpi
          Icon={TrendingUp}
          libelle={`Délai de résolution (${p.periode_resolution_jours}j)`}
          valeur={formaterDuree(kpi.delai_moyen_resolution_minutes)}
          aide={`${kpi.resolus_periode} résolus sur cette période`}
          couleur={tokens.success}
        />
        <Kpi
          Icon={AlertTriangle}
          libelle={`Tickets > ${p.seuil_alerte_jours_1} jours`}
          valeur={kpi.tickets_age_plus_x}
          aide="Non clôturés (alerte mineure)"
          couleur={tokens.warning}
        />
        <Kpi
          Icon={AlertOctagon}
          libelle={`Tickets > ${p.seuil_alerte_jours_2} jours`}
          valeur={kpi.tickets_age_plus_y}
          aide="Non clôturés (alerte majeure)"
          couleur={tokens.danger}
        />
      </div>

      {/* ===== Histogramme ===== */}
      <div style={{ ...cadre, marginBottom: 22 }}>
        <h3 style={titreSection}>
          <Activity size={14} /> Évolution sur 12 mois
        </h3>
        <Histogramme data={histogramme_mensuel} />
        <div style={{ display:'flex', gap: 16, justifyContent:'center', marginTop: 10, fontSize: 12, color: tokens.inkSoft }}>
          <span style={{ display:'flex', alignItems:'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: tokens.accent, borderRadius: 2 }}/> Tickets créés
          </span>
          <span style={{ display:'flex', alignItems:'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, background: tokens.success, borderRadius: 2 }}/> Tickets résolus
          </span>
        </div>
      </div>

      {/* ===== 3 listes ===== */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 14,
      }}>
        <ListeTickets
          titre={`En souffrance (> ${p.seuil_souffrance_jours}j)`}
          Icon={AlertCircle}
          tickets={en_souffrance}
          vide="Aucun ticket en souffrance 🎉"
          danger={en_souffrance.length > 0}
          onClic={onOuvrirTicket}
        />
        <ListeTickets
          titre="10 derniers créés (actifs)"
          Icon={Inbox}
          tickets={derniers_crees}
          vide="Aucun ticket actif"
          onClic={onOuvrirTicket}
        />
        <ListeTickets
          titre="10 derniers modifiés (actifs)"
          Icon={Edit3}
          tickets={derniers_modifies}
          vide="Aucun ticket actif"
          onClic={onOuvrirTicket}
        />
        <ListeTickets
          titre="10 derniers clôturés"
          Icon={Archive}
          tickets={derniers_clos || []}
          vide="Aucun ticket clos"
          onClic={onOuvrirTicket}
        />
      </div>
    </div>
  );
}

function Kpi({ Icon, libelle, valeur, aide, couleur }) {
  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 10,
      padding: 16,
      display: 'flex',
      gap: 14,
      alignItems: 'flex-start',
    }}>
      <div style={{
        flexShrink: 0,
        width: 36, height: 36, borderRadius: 8,
        background: couleur + '20',
        color: couleur,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <Icon size={18} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: tokens.inkMuted, textTransform:'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 2 }}>
          {libelle}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: tokens.ink, lineHeight: 1.1 }}>
          {valeur}
        </div>
        {aide && (
          <div style={{ fontSize: 11, color: tokens.inkMuted, marginTop: 2 }}>{aide}</div>
        )}
      </div>
    </div>
  );
}

function Histogramme({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => Math.max(d.crees, d.resolus)));

  const W = 600;
  const H = 200;
  const PAD_BAS = 28;
  const PAD_HAUT = 8;
  const slotW = W / data.length;
  const barW = (slotW - 6) / 2;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Ligne 0 */}
      <line x1={0} y1={H - PAD_BAS} x2={W} y2={H - PAD_BAS} stroke={tokens.border} strokeWidth={1} />

      {data.map((m, i) => {
        const x = i * slotW + 3;
        const hCree   = ((H - PAD_BAS - PAD_HAUT) * m.crees)   / max;
        const hResolu = ((H - PAD_BAS - PAD_HAUT) * m.resolus) / max;
        return (
          <g key={i}>
            <rect
              x={x} y={H - PAD_BAS - hCree}
              width={barW} height={hCree}
              fill={tokens.accent}
              rx={2}
            />
            <rect
              x={x + barW + 2} y={H - PAD_BAS - hResolu}
              width={barW} height={hResolu}
              fill={tokens.success}
              rx={2}
            />
            {m.crees > 0 && (
              <text
                x={x + barW / 2} y={H - PAD_BAS - hCree - 3}
                fontSize={9} fill={tokens.accent} textAnchor="middle" fontWeight={600}
              >{m.crees}</text>
            )}
            {m.resolus > 0 && (
              <text
                x={x + barW + 2 + barW / 2} y={H - PAD_BAS - hResolu - 3}
                fontSize={9} fill={tokens.success} textAnchor="middle" fontWeight={600}
              >{m.resolus}</text>
            )}
            <text
              x={i * slotW + slotW / 2} y={H - 10}
              fontSize={10} fill={tokens.inkSoft} textAnchor="middle"
            >{m.libelle}</text>
          </g>
        );
      })}
    </svg>
  );
}

function ListeTickets({ titre, Icon, tickets, vide, danger, onClic }) {
  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${danger ? tokens.danger + '40' : tokens.border}`,
      borderRadius: 10,
      padding: 14,
    }}>
      <h3 style={{
        ...titreSection,
        color: danger ? tokens.danger : tokens.inkSoft,
        marginBottom: 10,
      }}>
        <Icon size={14} /> {titre} {tickets.length > 0 && <span style={{ color: tokens.inkMuted, fontWeight: 500 }}>({tickets.length})</span>}
      </h3>
      {tickets.length === 0 ? (
        <div style={{ fontSize: 12, color: tokens.inkMuted, padding: '20px 0', textAlign:'center' }}>
          {vide}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
          {tickets.map(t => <Ligne key={t.id} ticket={t} onClic={() => onClic(t.token_public)} />)}
        </div>
      )}
    </div>
  );
}

function Ligne({ ticket, onClic }) {
  const statut = couleursStatut[ticket.statut] || couleursStatut.nouveau;
  const gravite = ticket.gravite_ia ? couleursGravite[ticket.gravite_ia] : null;
  const cheminService = ticket.parent_service_nom
    ? `${ticket.parent_service_nom} / ${ticket.service_nom}`
    : ticket.service_nom;

  return (
    <div
      onClick={onClic}
      style={{
        padding: '7px 9px',
        borderRadius: 6,
        cursor:'pointer',
        background: tokens.bg,
        transition: 'background 0.1s',
        display:'flex', flexDirection:'column', gap: 2,
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = tokens.accentBg}
      onMouseLeave={(e) => e.currentTarget.style.background = tokens.bg}
    >
      <div style={{ display:'flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
        <span style={{ fontFamily:'monospace', fontSize: 10, color: tokens.inkMuted }}>
          #{ticket.token_public}
        </span>
        <Badge bg={statut.bg} fg={statut.fg} taille="petit">{statut.label}</Badge>
        {ticket.type && couleursType[ticket.type] && (
          <Badge bg={couleursType[ticket.type].bg} fg={couleursType[ticket.type].fg} taille="petit">
            {couleursType[ticket.type].label}
          </Badge>
        )}
        {ticket.source === 'sentry' && (
          <Badge bg={tokens.warningBg} fg={tokens.warning} taille="petit">
            📊{ticket.sentry_occurrences ? ` ${ticket.sentry_occurrences}×` : ''}
          </Badge>
        )}
        {gravite && <Badge bg={gravite.bg} fg={gravite.fg} taille="petit">{ticket.gravite_ia}</Badge>}
        <span style={{ marginLeft:'auto', fontSize: 10, color: tokens.inkMuted }}>
          {formaterDateRelative(ticket.cree_le)}
        </span>
      </div>
      <div style={{
        fontSize: 12, color: tokens.ink, lineHeight: 1.3,
        overflow:'hidden', textOverflow:'ellipsis',
        display:'-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient:'vertical',
      }}>
        {ticket.resume_ia || '(sans résumé)'}
      </div>
      <div style={{ fontSize: 10, color: tokens.inkMuted }}>
        {ticket.source === 'sentry' ? 'Sentry' : (ticket.declarant_nom || '—')} · {cheminService}
      </div>
    </div>
  );
}

function formaterDuree(minutes) {
  if (!minutes || minutes <= 0) return '—';
  if (minutes < 60) return Math.round(minutes) + ' min';
  const h = Math.floor(minutes / 60);
  if (h < 24) {
    const m = Math.round(minutes - h * 60);
    return h + 'h' + (m > 0 ? ' ' + m : '');
  }
  const j = Math.floor(h / 24);
  const restH = Math.round(h - j * 24);
  return j + 'j' + (restH > 0 ? ' ' + restH + 'h' : '');
}

const cadre = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 18,
};
const titreSection = {
  margin: 0, fontSize: 12, fontWeight: 600,
  color: tokens.inkSoft, textTransform:'uppercase', letterSpacing: 0.5,
  display:'flex', alignItems:'center', gap: 6,
  marginBottom: 14,
};
const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32, textAlign:'center', color: tokens.inkSoft, fontSize: 14,
};
